<template>
  <modal @mounted="initCanvas()">
    <div class="memoram-instance">
      <button @click="$emit('closeGame', false)" class="memoram-instance-close">
        <font-awesome-icon :icon="['fas', 'times']" size="2x" />
      </button>
      <div class="memoram-instance-data text-center">
        <h2 class="memoram-instance-data-title">MEMORAM</h2>
        <div class="memoram-instance-data-content">
          <h3 class="memoram-instance-data-content-text">
            {{ $t("portfolio.memoram.instance.foundLabel") }}
          </h3>
          <h3 class="memoram-instance-data-content-text dynamic">
            <b>{{ game.founded }}/{{ questions.length }}</b>
          </h3>
          <h3 class="memoram-instance-data-content-text">
            {{ $t("portfolio.memoram.instance.timeLabel") }}
          </h3>
          <h3 class="memoram-instance-data-content-text dynamic">
            <b>{{ formattedTime }}</b>
          </h3>
        </div>
      </div>
      <template v-if="!!game.status">
        <div class="memoram-instance-backdrop"></div>
        <div class="memoram-instance-final-message">
          <h1>{{ $t("portfolio.memoram.instance.overLabel") }}</h1>
          <h2>{{ $t("portfolio.memoram.instance.totalTimeLabel") }} {{ formattedTime }}</h2>
        </div>
        <button @click="resetGame" class="memoram-instance-restart">
          <b>{{ $t("portfolio.memoram.instance.restartLabel") }}</b>
        </button>
      </template>
      <div ref="VirtualContainer" class="memoram-instance-3d"></div>
    </div>
  </modal>
</template>

<script>
import * as THREE from "three";
import World from "@/models/Memoram/World";
import ResourceTracker from "@/models/ResourceTracker";

const Modal = () => import(/* webpackChunkName: "Modal" */ "@/components/Global/Modal.vue");

export default {
  name: "MemoramInstance",
  components: {
    Modal
  },
  data() {
    return {
      resourceTracker: new ResourceTracker(),
      camera: null,
      scene: null,
      renderer: null,
      frameId: null,
      game: {
        status: false, // false game continues, true game won
        founded: 0,
        time: {
          id: null,
          hour: 0,
          minute: 0,
          second: 0
        }
      }
    };
  },
  computed: {
    /*
     * @computed - formattedTime
     * Gets formatted time to display in memoram data box
     */
    formattedTime() {
      const { hour, minute, second } = this.game.time;
      const formattedHour = (hour < 10 ? "0" : "") + hour;
      const formattedMinute = (minute < 10 ? "0" : "") + minute;
      const formattedSecond = (second < 10 ? "0" : "") + second;

      return `${formattedHour}:${formattedMinute}:${formattedSecond}`;
    },
    /*
     * @computed - questions
     * Gets translated questions
     */
    questions() {
      const questions = [
        { id: 1, path: "/img/3DTextures/memoram/game/special-relativity.jpg" },
        { id: 2, path: "/img/3DTextures/memoram/game/motor.jpg" },
        { id: 3, path: "/img/3DTextures/memoram/game/9.jpg" },
        { id: 4, path: "/img/3DTextures/memoram/game/cold.jpg" },
        { id: 5, path: "/img/3DTextures/memoram/game/juegos-olimpicos.png" },
        { id: 6, path: "/img/3DTextures/memoram/game/torre-de-pisa.jpeg" },
        { id: 7 },
        { id: 8, path: "/img/3DTextures/memoram/game/baloncesto.jpg" },
        { id: 9 },
        { id: 10 },
        { id: 11, path: "/img/3DTextures/memoram/game/escarabajo.jpg" },
        { id: 12, path: "/img/3DTextures/memoram/game/food.jpg" }
      ];

      if (
        this.$t("portfolio.memoram.instance.questions") &&
        this.$t("portfolio.memoram.instance.questions").length
      ) {
        return questions.map((question, i) => {
          const text = question.text || this.$t("portfolio.memoram.instance.questions")[i] || "";

          return { ...question, text };
        });
      }

      return questions;
    },
    /*
     * @computed - answers
     * Gets translated answers
     */
    answers() {
      const answers = [
        {
          id: 1,
          text: "Albert Einstein",
          path: "/img/3DTextures/memoram/game/albert-einstein.jpg"
        },
        { id: 2, text: "Nikola Tesla", path: "/img/3DTextures/memoram/game/nikola-tesla.jpeg" },
        { id: 3, path: "/img/3DTextures/memoram/game/3.jpg" },
        { id: 4, path: "/img/3DTextures/memoram/game/antartida.jpg" },
        { id: 5, path: "/img/3DTextures/memoram/game/olimpia-grecia.jpg" },
        { id: 6, path: "/img/3DTextures/memoram/game/italia.jpg" },
        { id: 7, path: "/img/3DTextures/memoram/game/rusia.jpg" },
        { id: 8, text: "Michael Jordan", path: "/img/3DTextures/memoram/game/michael-jordan.jpeg" },
        { id: 9, path: "/img/3DTextures/memoram/game/5.jpg" },
        { id: 10, path: "/img/3DTextures/memoram/game/pancreas.png" },
        { id: 11, path: "/img/3DTextures/memoram/game/coleoptero.png" },
        { id: 12, path: "/img/3DTextures/memoram/game/diet.jpg" }
      ];

      if (
        this.$t("portfolio.memoram.instance.answers") &&
        this.$t("portfolio.memoram.instance.answers").length
      ) {
        return answers.map((answer, i) => {
          const text = answer.text || this.$t("portfolio.memoram.instance.answers")[i] || "";

          return { ...answer, text };
        });
      }

      return answers;
    }
  },
  methods: {
    /*
     * @function - initCanvas
     * This function initialize scene, camera, and renderer for memoram instance
     * @param - none
     */
    initCanvas() {
      const { VirtualContainer } = this.$refs;
      const { offsetWidth, offsetHeight } = VirtualContainer;

      // Camera
      this.camera = this.resourceTracker.track(
        new THREE.PerspectiveCamera(75, offsetWidth / offsetHeight, 0.1, 1000)
      );

      // Scene
      const reorderedQuestions = [];
      const reorderedAnswers = [];
      const reorderedQuestionIndexes = this.getRandomArray(this.questions.length);
      const reorderedAnswerIndexes = this.getRandomArray(this.answers.length);

      reorderedQuestionIndexes.forEach(i => reorderedQuestions.push({ ...this.questions[i] }));
      reorderedAnswerIndexes.forEach(i => reorderedAnswers.push({ ...this.answers[i] }));

      this.scene = this.resourceTracker.track(
        World(reorderedQuestions, reorderedAnswers, this.camera, VirtualContainer, this.$bus)
      );

      // Renderer
      this.renderer = this.resourceTracker.track(new THREE.WebGLRenderer({ alpha: true }));
      this.renderer.setPixelRatio(window.devicePixelRatio);
      this.renderer.setSize(offsetWidth, offsetHeight);
      VirtualContainer.appendChild(this.renderer.domElement);

      this.animate();
      this.doEvents();
      this.game.time.id = setInterval(this.setGameTime, 1000);
    },
    /*
     * @function - animateEntry
     * Sets scene animation loop with sense and perform methods (agents programming orientation)
     * @param - none
     */
    animate() {
      this.frameId = requestAnimationFrame(this.animate);

      if (this.renderer) {
        this.renderer.render(this.scene, this.camera);

        this.scene.children.forEach(child => {
          if (child.sensing) {
            child.sensing(this.scene);
          }
          if (child.perform) {
            child.perform(this.scene);
          }
        });
      }
    },
    /*
     * @function - doEvents
     * Adds or removes all dom listeners of any 3D model if exists
     * @param - add (Boolean): if TRUE adds event listeners, otherwise removes them
     */
    doEvents(add = true) {
      const { VirtualContainer } = this.$refs;

      this.scene.children.forEach(child => {
        if (child.mouseMove) {
          VirtualContainer[add ? "addEventListener" : "removeEventListener"](
            "mousemove",
            child.mouseMove
          );
        }
        if (child.click) {
          VirtualContainer[add ? "addEventListener" : "removeEventListener"]("click", child.click);
        }
      });
    },
    /*
     * @function - doResize
     * Redefine 3d scene size when windiw is resized
     * @param - none
     */
    doResize() {
      const { offsetWidth, offsetHeight } = this.$refs.VirtualContainer;

      this.camera.aspect = offsetWidth / offsetHeight;
      this.camera.updateProjectionMatrix();

      this.renderer.setSize(offsetWidth, offsetHeight);
    },
    /*
     * @function - removeCanvas
     * Clears all 3D scene memory dependencies, remove scene dom element and cancel animation loop
     * @param - none
     */
    removeCanvas() {
      this.doEvents(false);
      this.resourceTracker.dispose();
      this.$refs.VirtualContainer.removeChild(this.renderer.domElement);
      cancelAnimationFrame(this.frameId);
      this.frameId = null;
    },
    /*
     * @function - resetGame
     * Clears all 3D scene, reset game data to initial state and recreate 3D canvas
     * @param - none
     */
    resetGame() {
      this.removeCanvas();
      this.finishGame(false);
      this.setGameFounded(0);
      this.setGameTime(true);
      this.initCanvas();
    },
    /*
     * @function - getRandomArray
     * Randomize an array of indexes from 0 to max value
     * @param - max (Number): Value to define number of elements and value inside randomized array
     */
    getRandomArray(max) {
      const unexistentNumber = []; // Will save unexisting numbers in first random process
      const repeatedNumber = []; // Will save repeated numbers in first random process
      const numberOfRepetitions = []; // Will save number of repetitions of every number in repatedNumber
      const reorderedArray = []; // Will contain randomized values

      for (let i = 0; i < max; i++) {
        // First random process
        reorderedArray[i] = Math.floor(Math.random() * max); // From 0 to max
      }

      let counter = 0; // Will tell if a number does not exist (0), is only once (1), or how many times is repeated (>1)
      let unexistentKey = 0; // Index of unexistentNumber
      let repeatedKey = 0; // Index of repeatedNumber and numberOfRepetitions
      for (let i = 0; i < max; i++) {
        // Check unexisting or repeated values
        for (let j = 0; j < max; j++) {
          // Sweep defined by max and reorderedArray vars
          if (i === reorderedArray[j]) {
            // If any of the nominal range exists in reorderedArray, counter will be added one by one
            counter++;
          }
        }
        if (counter === 0) {
          // Unexistent number
          unexistentNumber[unexistentKey] = i; // Saving unexistent number
          unexistentKey++; // Increment by one
        } else if (counter > 1) {
          // Repeated number
          repeatedNumber[repeatedKey] = i; // Saving repeated number
          numberOfRepetitions[repeatedKey] = counter - 1; // Saving repetition minus the one that should exists
          repeatedKey++; // Increment by one
        }
        counter = 0; // Reinit
      }

      if (repeatedKey !== 0 || unexistentKey !== 0) {
        // If first randomize made any repetition or lack of number
        let fixedKey = 0; // Will decide how many repeated numbers will be changedaccording to numberOfRepetitions
        unexistentKey = 0; // Defines index of unexistentNumber
        for (let i = repeatedNumber.length - 1; i >= 0; i--) {
          // Descendent for that sets range according to repeatedNumber length
          for (let j = 0; j < max; j++) {
            // For that will sweep all reorderedArray
            if (fixedKey < numberOfRepetitions[i]) {
              // If a number is repeated, will be modified to leave it only once
              if (reorderedArray[j] === repeatedNumber[i]) {
                // Finding repeated number in reorderedArray
                reorderedArray[j] = unexistentNumber[unexistentKey]; // Redefine repeated with unexisting numbers
                unexistentKey++;
                fixedKey++;
              }
            }
          }
          fixedKey = 0;
        }
      }

      return reorderedArray;
    },
    /*
     * @function - setGameTime
     * Increase time in one second at a time, or resets it to zero
     * @param - reset (Boolean): if TRUE resets time to 0
     */
    setGameTime(reset = false) {
      this.game.time.second++;
      if (this.game.time.second === 60) {
        this.game.time.second = 0;
        this.game.time.minute++;
      }

      if (this.game.time.minute === 60) {
        this.game.time.minute = 0;
        this.game.time.hour++;
      }

      if (reset) {
        this.game.time.second = 0;
        this.game.time.minute = 0;
        this.game.time.hour = 0;
      }
    },
    /*
     * @function - setGameFounded
     * Sets number of cards founded
     * @param - num (Number): Value of cards founded
     */
    setGameFounded(num) {
      this.game.founded = num;
    },
    /*
     * @function - finishGame
     * Sets game status to finished or active
     * @param - status (Boolean): if TRUE game is finished, otherwise will be used to reset game
     */
    finishGame(status = true) {
      clearInterval(this.game.time.id);
      this.game.time.id = null;
      this.game.status = status;
    }
  },
  created() {
    this.$bus.$on("Memoram: set game cards founded", this.setGameFounded);
    this.$bus.$on("Memoram: game finished", this.finishGame);
  },
  mounted() {
    window.addEventListener("resize", this.doResize);
  },
  beforeDestroy() {
    this.removeCanvas();
    this.$bus.$off("Memoram: set game cards founded", this.setGameFounded);
    this.$bus.$off("Memoram: game finished", this.finishGame);
    window.removeEventListener("resize", this.doResize);

    if (this.game.time.id) {
      clearInterval(this.game.time.id);
      this.game.time.id = null;
    }
  }
};
</script>

<style lang="scss" scoped>
.memoram-instance {
  height: 100%;
  position: relative;
  width: 100%;

  &-data {
    background-color: #000000;
    border-radius: 0.75rem 0.75rem 1.5rem 1.5rem;
    left: 0.5rem;
    opacity: 0.8;
    padding: 0.25rem 0;
    position: absolute;
    top: 0.5rem;

    &-title {
      color: darkturquoise;
      font-family: serif;
    }

    &-content {
      background-color: #220000;
      border-radius: 0.75rem 0.75rem 1.5rem 1.5rem;
      padding: 0 0.25rem;

      &-text {
        color: aquamarine;
        font-family: cursive;

        &.dynamic {
          color: chartreuse;
        }
      }
    }
  }

  &-final-message {
    -webkit-transition: width 0.5s, height 0.5s, color 0.5s, text-shadow 0.5s, top 0.5s, left 0.5s,
      padding-top 0.5s, font-size 0.5s; /* For Safari 3.1 to 6.0 */
    background-color: rgba(150, 150, 150, 0);
    border-radius: 10px;
    color: aqua;
    font-family: cursive;
    height: 15%;
    left: 15%;
    position: absolute;
    text-align: center;
    text-decoration: none;
    text-shadow: 3px -3px #003399;
    top: 15%;
    transition: width 0.5s, height 0.5s, color 0.5s, text-shadow 0.5s, top 0.5s, left 0.5s,
      padding-top 0.5s, font-size 0.5s;
    width: 70%;
    z-index: 4;
  }

  &-restart {
    display: block;
    border-radius: 10px;
    background-color: crimson;
    position: absolute;
    height: 90px;
    width: 30%;
    top: 45%;
    left: 35%;
    font-size: x-large;
    color: aquamarine;
    z-index: 4;

    :hover {
      text-decoration: none;
      color: aqua;
    }
  }

  &-backdrop {
    -webkit-transition: width 0.5s, height 0.5s, color 0.5s, text-shadow 0.5s, top 0.5s, left 0.5s,
      padding-top 0.5s, font-size 0.5s; /* For Safari 3.1 to 6.0 */
    background-color: rgba(0, 0, 0, 0.8);
    bottom: 0;
    color: rgba(200, 0, 0, 0.7);
    font-family: Papyrus, fantasy;
    left: 0;
    right: 0;
    text-align: center;
    text-decoration: none;
    text-shadow: 5px 5px rgb(0, 0, 0);
    top: 0;
    position: absolute;
    transition: width 0.5s, height 0.5s, color 0.5s, text-shadow 0.5s, top 0.5s, left 0.5s,
      padding-top 0.5s, font-size 0.5s;
    z-index: 3;
  }

  &-close {
    background-color: rgba(#220000, 0.8);
    border-radius: 0.75rem;
    color: rgba(chartreuse, 0.7);
    margin: 0.25rem 0.5rem 0.25rem 0.25rem;
    padding: 0.5rem;
    position: absolute;
    right: 0;
    z-index: 4;
  }

  &-3d,
  canvas {
    width: 100%;
    height: 100%;
  }
}
</style>
